import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import {
  cbpLookupCache,
  fetchAirportCountry,
  isUsIcao,
  normalizeCountry,
} from "@/lib/newTripConstants";

// Encapsulates the two effects that read leg origin/destination strings and
// (1) enrich each leg with CBP airport info when the destination is a US
//     fact-sheet airport AND origin is non-US;
// (2) auto-toggle `is_international` based on origin/destination country
//     comparison.
//
// `setLegs` is invoked with the same functional updater shape that lived in
// the original component (no behavior change).
export default function useCbpEnrichment(legs, setLegs) {
  const cbpDepKey = useMemo(
    () =>
      legs
        .map(
          (l) =>
            `${(l.origin || "").trim().toUpperCase()}>${(l.destination || "").trim().toUpperCase()}`
        )
        .join("|"),
    [legs]
  );

  // Debounce: every keystroke in origin/destination changed `cbpDepKey` and
  // re-walked all legs (with a network round-trip on every 4-char ICAO).
  // 250ms is the typical "I stopped typing" pause without feeling laggy.
  const [debouncedKey, setDebouncedKey] = useState(cbpDepKey);
  useEffect(() => {
    const id = setTimeout(() => setDebouncedKey(cbpDepKey), 250);
    return () => clearTimeout(id);
  }, [cbpDepKey]);

  // CBP destination lookup — only relevant for international inbounds.
  // Gated on origin being a non-US ICAO because CBP inspection applies
  // to flights *arriving* from abroad; a domestic leg (KJFK->KLAX) does
  // not need CBP info surfaced even if KLAX has a fact sheet. When the
  // gate passes and the destination is a valid 4-char ICAO, we fetch
  // `/cbp/airports/{icao}`: a 200 attaches the sheet to the leg (office
  // hours + landing rights) and auto-checks `is_international`; a 404
  // is cached as null so the fetch never repeats for that ICAO.
  // Re-triggers when any leg's origin OR destination string changes.
  useEffect(() => {
    const controllers = [];
    legs.forEach((leg, idx) => {
      const origin = (leg.origin || "").trim().toUpperCase();
      const icao = (leg.destination || "").trim().toUpperCase();
      const validIcao = /^[A-Z]{4}$/.test(icao);

      // Suppress the CBP card entirely when the origin is itself in the
      // US — this is a domestic flight. Also drops the card when origin
      // is missing: without knowing the origin we can't say whether the
      // arrival is international, so err on the side of not showing.
      const originIsUs = isUsIcao(origin);
      const originDefined = origin.length === 4;
      const suppress = !validIcao || !originDefined || originIsUs;

      // Destination cleared, invalid, or origin is US -> wipe any stale CBP
      // info so the info card doesn't linger on top of a new destination.
      if (suppress) {
        if (leg.cbp_info || leg._cbp_checked) {
          setLegs((prev) => {
            const next = [...prev];
            if (next[idx]) next[idx] = { ...next[idx], cbp_info: null, _cbp_checked: null };
            return next;
          });
        }
        return;
      }

      // Already looked this ICAO up for this leg — nothing to do.
      if (leg._cbp_checked === icao) return;

      // Apply from cache synchronously if we've seen this ICAO before.
      if (cbpLookupCache.has(icao)) {
        const cached = cbpLookupCache.get(icao);
        setLegs((prev) => {
          const next = [...prev];
          if (next[idx] && (next[idx].destination || "").trim().toUpperCase() === icao) {
            next[idx] = {
              ...next[idx],
              _cbp_checked: icao,
              cbp_info: cached,
              is_international: cached ? true : next[idx].is_international,
            };
          }
          return next;
        });
        return;
      }

      const controller = new AbortController();
      controllers.push(controller);
      api
        .get(`/cbp/airports/${icao}`, { signal: controller.signal })
        .then((res) => {
          cbpLookupCache.set(icao, res.data);
          setLegs((prev) => {
            const next = [...prev];
            if (next[idx] && (next[idx].destination || "").trim().toUpperCase() === icao) {
              next[idx] = {
                ...next[idx],
                _cbp_checked: icao,
                cbp_info: res.data,
                is_international: true,
              };
            }
            return next;
          });
        })
        .catch((err) => {
          if (controller.signal.aborted) return;
          // A 404 is an answer — this airport has no CBP fact sheet — so it is
          // safe to cache. Any other failure (500, timeout, offline) means we
          // don't know, so don't poison the cache with a null.
          const isDefinitive = err.response?.status === 404;
          if (isDefinitive) cbpLookupCache.set(icao, null);
          // Either way the stale value must go: it belongs to the PREVIOUS
          // destination, and leaving it renders another airport's customs
          // hours and landing rights on this leg.
          setLegs((prev) => {
            const next = [...prev];
            if (next[idx] && (next[idx].destination || "").trim().toUpperCase() === icao) {
              next[idx] = {
                ...next[idx],
                // Only mark it checked when we got a definitive answer, so a
                // transient failure retries instead of sticking as "no CBP".
                ...(isDefinitive ? { _cbp_checked: icao } : {}),
                cbp_info: null,
              };
            }
            return next;
          });
        });
    });
    return () => controllers.forEach((c) => c.abort());
    // `legs` intentionally read stale inside — the effect only needs to
    // re-run when an origin or destination string actually changes, and
    // cbpDepKey is what captures that.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedKey]);

  // Auto-mark `is_international` whenever origin/destination resolve to
  // different countries. Symmetric: countries match -> unmark. Either
  // country unknown (airport not in dataset) -> leave the user's toggle
  // alone. Runs in parallel to the CBP effect above; both end up agreeing
  // on `true` for non-US -> US legs, so there's no flip-flop.
  useEffect(() => {
    let cancelled = false;
    legs.forEach((leg, idx) => {
      const o = (leg.origin || "").trim().toUpperCase();
      const d = (leg.destination || "").trim().toUpperCase();
      if (!/^[A-Z]{4}$/.test(o) || !/^[A-Z]{4}$/.test(d)) return;
      Promise.all([fetchAirportCountry(o), fetchAirportCountry(d)]).then(([oc, dc]) => {
        if (cancelled) return;
        if (!oc || !dc) return;
        const intl = normalizeCountry(oc) !== normalizeCountry(dc);
        setLegs((prev) => {
          const next = [...prev];
          if (next[idx] && next[idx].is_international !== intl) {
            next[idx] = { ...next[idx], is_international: intl };
          }
          return next;
        });
      });
    });
    return () => {
      cancelled = true;
    };
    // `legs` se lee adentro pero solo nos importa re-correr cuando una
    // origen/destino realmente cambia; cbpDepKey captura eso. Incluir legs
    // dispararia el efecto en cada keystroke de campos no-ICAO.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedKey]);
}
